/** @file Mesh layer: triangle edges, hull loop, vertex dots (selection) and vertex-id labels. */
import type { MeshAttachmentData } from '@pose2d/core/types';
import type { Sink } from '../draw/createSink';
import { emitDisc } from '../draw/emitDisc';
import { emitLine } from '../draw/emitLine';
import { fillSegment } from '../draw/fillSegment';
import { anchorInView } from '../labels/anchorInView';
import type { LabelRequest } from '../labels/placeLabels';
import type { OverlayInput } from '../overlayInput';
import type { OverlaySelection } from '../overlayOptions';
import { OVERLAY_STYLE } from '../overlayStyle';
import { edgeKeys } from './edgeKeys';
import { meshPixels } from './meshPixels';
import { meshScratch } from './meshScratch';

const line = new Float64Array(4);
const NONE: readonly number[] = [];
const NO_VERTICES: readonly [number, number][] = [];
/**
 * Vertex-id label requests reused across builds (no object per label once warm); `taken` are in use this call. Only
 * ids the layer can place are queued (at most `OVERLAY_STYLE.maxLabelsPerLayer`), so the pool stays within that.
 */
const pool: LabelRequest[] = [];
let taken = 0;

/** True when i-j is an edge of the hull loop 0 -> 1 -> ... -> hull-1 -> 0. */
function isHullEdge(i: number, j: number, hull: number): boolean {
  return i < hull && j < hull && (Math.abs(i - j) === 1 || Math.abs(i - j) === hull - 1);
}

/** Line between vertices a and b of `px`. */
function edge(sink: Sink, px: Float64Array, a: number, b: number, width: number, color: number): void {
  emitLine(sink, fillSegment(line, px[2 * a] ?? NaN, px[2 * a + 1] ?? NaN, px[2 * b] ?? NaN, px[2 * b + 1] ?? NaN), width, color);
}

/** Interior triangle edges once each (deduplicated in the mesh's edge table), then the hull loop on top. */
function drawWire(sink: Sink, mesh: MeshAttachmentData, px: Float64Array, selected: boolean): void {
  const tris = mesh.triangles, table = meshScratch.forMesh(mesh.uvs.length, tris.length).table;
  const mask = edgeKeys.reset(table, tris.length);
  for (let t = 0; t + 2 < tris.length; t += 3) {
    for (let e = 0; e < 3; e++) {
      const a = tris[t + e] ?? 0, b = tris[t + ((e + 1) % 3)] ?? 0;
      if (isHullEdge(a, b, mesh.hull) || !edgeKeys.add(table, mask, Math.min(a, b) * 65536 + Math.max(a, b))) continue;
      edge(sink, px, a, b, 1, selected ? OVERLAY_STYLE.selected : OVERLAY_STYLE.meshEdge);
    }
  }
  for (let i = 0; i < mesh.hull; i++) edge(sink, px, i, (i + 1) % mesh.hull, OVERLAY_STYLE.lineWidth, selected ? OVERLAY_STYLE.selected : OVERLAY_STYLE.hullEdge);
}

/** Marks the vertices of attachment `att` listed in `vertices` (count = vertices of the mesh); returns the marks. */
function pick(mesh: MeshAttachmentData, vertices: readonly [number, number][], att: number, count: number): Uint8Array {
  const picked = meshScratch.forMesh(mesh.uvs.length, mesh.triangles.length).picked;
  picked.fill(0, 0, count);
  for (let k = 0; k < vertices.length; k++) {
    const entry = vertices[k];
    if (entry === undefined || entry[0] !== att) continue;
    const v = entry[1];
    if (Number.isInteger(v) && v >= 0 && v < count) picked[v] = 1;
  }
  return picked;
}

/** A dot per vertex (`n` = the mesh's uvs.length); selected vertices of this attachment larger and yellow. */
function drawDots(sink: Sink, px: Float64Array, n: number, picked: Uint8Array): void {
  for (let i = 0; 2 * i < n; i++) {
    const on = picked[i] === 1;
    emitDisc(sink, px[2 * i] ?? NaN, px[2 * i + 1] ?? NaN, on ? 4 : OVERLAY_STYLE.vertexRadius, on ? OVERLAY_STYLE.selected : OVERLAY_STYLE.vertex);
  }
}

/** Queues the id of vertex i at (x, y) from the label pool. */
function queue(labels: LabelRequest[], i: number, x: number, y: number): void {
  const label = pool[taken] ?? { text: '', x: 0, y: 0, color: 0 };
  pool[taken++] = label;
  label.text = String(i);
  label.x = x;
  label.y = y;
  label.color = OVERLAY_STYLE.labelText;
  labels.push(label);
}

/**
 * Index labels for every vertex of one mesh (`n` = its uvs.length). Placement skips an anchor outside the view and
 * drops what the layer places past the cap, so such ids are not queued at all: the ones past the cap are counted in
 * `sink.droppedLabels` instead (the same count, and nothing per vertex allocated for them).
 */
function vertexIds(sink: Sink, px: Float64Array, n: number, labels: LabelRequest[]): void {
  for (let i = 0; 2 * i < n; i++) {
    const x = px[2 * i] ?? NaN, y = px[2 * i + 1] ?? NaN;
    if (!anchorInView(x, y, sink.width, sink.height)) continue;
    if (taken < OVERLAY_STYLE.maxLabelsPerLayer) queue(labels, i, x, y);
    else sink.droppedLabels += 1;
  }
}

/** Wire (yellow when the slot is selected) and dots (selected vertices of attachment `att` enlarged). */
function wireAndDots(sink: Sink, selection: OverlaySelection | undefined, slot: number, att: number, mesh: MeshAttachmentData, px: Float64Array): void {
  drawWire(sink, mesh, px, (selection?.slots ?? NONE).includes(slot));
  drawDots(sink, px, mesh.uvs.length, pick(mesh, selection?.vertices ?? NO_VERTICES, att, Math.ceil(mesh.uvs.length / 2)));
}

/** Wire, dots and ids for the mesh shown by `slot` (nothing for regions and empty slots). */
function drawSlot(sink: Sink, input: OverlayInput, slot: number, labels: LabelRequest[]): void {
  const att = input.pose.slotAttachment[slot] ?? -1;
  const mesh = input.data.attachments[att];
  const px = meshPixels(input, slot, att);
  if (mesh?.type !== 'mesh' || px === null) return;
  if (input.options.mesh === true) wireAndDots(sink, input.options.selection, slot, att, mesh, px);
  if (input.options.vertexIds === true) vertexIds(sink, px, mesh.uvs.length, labels);
}

/**
 * `options.mesh` (wire + dots) and `options.vertexIds` (index labels) for every visible mesh, in draw order. The
 * vertex-id requests are pooled objects refilled by the next call: buildOverlay places and draws them first.
 */
export function drawMesh(sink: Sink, input: OverlayInput, labels: LabelRequest[]): void {
  if (input.options.mesh !== true && input.options.vertexIds !== true) return;
  taken = 0;
  const order = input.pose.drawOrder;
  for (let k = 0; k < order.length; k++) drawSlot(sink, input, order[k] ?? -1, labels);
}
